import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from '@chakra-ui/react';
import Icon from '@mdi/react';
import { mdiArrowRight } from '@mdi/js';
import { Visit } from '@/pages/types';
import { Button } from '@/trailmix/Button';
import { getImageFromCDN } from '@/image-cdn';
import { getDateTimeStringForDisplay } from '@/datetime/utils';
import { ProseDisplay } from '@/components/Tiptap';
import { PhotoGrid } from '@/trailmix/PhotoGrid';
import { Rating } from '@/trailmix/Rating';

export function VisitSelectionModal({
    visits,
    isOpen,
    onClose,
    onSelectVisit,
}: {
    visits: Visit[];
    isOpen: boolean;
    onClose: () => void;
    onSelectVisit: (visit: Visit) => void;
}) {
    return (
        <Modal isOpen={isOpen} onClose={onClose} size='xl' scrollBehavior='inside'>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader pb={4}>Link a visit</ModalHeader>
                <ModalCloseButton />
                <ModalBody pt={0}>
                    <p className='text-gray-600'>
                        Link a visit for this place to showcase it inside this list. Linked visits also appear in this
                        list&apos;s <span className='font-medium'>Timeline</span> view.
                    </p>
                    <div className='mt-4 flex flex-col gap-4'>
                        {visits.map(visit => (
                            <button
                                key={visit.id}
                                onClick={() => {
                                    onClose();
                                    onSelectVisit(visit);
                                }}
                                className='flex flex-col gap-4 rounded-md border border-dashed border-green-500 bg-green-50 px-3 py-2 text-start shadow-sm'
                            >
                                <div className='flex flex-col gap-2'>
                                    <p className='font-semibold text-green-900'>
                                        {visit.visit_date ? (
                                            <>
                                                Visited on{' '}
                                                {getDateTimeStringForDisplay({
                                                    dateISO: visit.visit_date,
                                                    timeISO: visit.visit_time,
                                                })}
                                            </>
                                        ) : (
                                            <>No date</>
                                        )}
                                    </p>

                                    {visit.rating ? (
                                        <Rating value={visit.rating} />
                                    ) : (
                                        <p className='text-green-800'>No rating</p>
                                    )}
                                </div>

                                {visit.notes && <ProseDisplay content={visit.notes} className='text-green-900' />}

                                {visit.photos.length > 0 && (
                                    <PhotoGrid
                                        photos={visit.photos.map(photo => ({
                                            id: photo.id,
                                            originalURL: getImageFromCDN({
                                                path: photo.path,
                                                size: 'original',
                                            }).toURL(),
                                            thumbnailURL: getImageFromCDN({ path: photo.path, size: 'medium' }).toURL(),
                                        }))}
                                    />
                                )}

                                <div className='flex items-center gap-1 text-green-800'>
                                    Link this visit <Icon path={mdiArrowRight} className='size-4' />
                                </div>
                            </button>
                        ))}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} variant='secondary' className='grow'>
                        Close
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
